import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronRight,
  ChevronLeft,
  Cloud,
  Cpu,
  Users,
  BarChart3,
  Shield,
  Zap,
  Code,
  Settings,
  Eye,
  Target,
  Rocket,
  Star,
  Sparkles,
  ArrowRight,
  Play,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import VideoBackground from "@/components/layout/VideoBackground";
import HeroAnimated from "@/components/landing/HeroAnimated";
import FeatureStack from "@/components/landing/FeatureStack";
import { landingTestimonials } from "@/data/testimonials";

import Section from "@/components/shared/Section";
import SectionHeader from "@/components/shared/SectionHeader";
import Eyebrow from "@/components/shared/Eyebrow";
import Reveal from "@/components/shared/Reveal";
import GlowCard from "@/components/shared/GlowCard";
import GlowOrb from "@/components/shared/GlowOrb";
import AnimatedCounter from "@/components/shared/AnimatedCounter";

/* ----------------------- Static content ---------------------------------- */

const HERO_SLIDES = [
  {
    image: "https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&w=1920",
    title: "AI-Accelerated Digital Engineering",
    highlight: "for Mobile, Web, and Salesforce",
    subtitle: "We build production-ready apps, websites, and Salesforce automations using AI agents and LLM workflows.",
  },
  {
    image: "https://images.unsplash.com/photo-1655393001768-d946c97d6fd1?auto=format&fit=crop&w=1920&q=80",
    title: "Mobile Application Development",
    highlight: "Built with AI from Day One",
    subtitle: "Launch iOS and Android products faster with AI-assisted engineering, testing, and in-app intelligence.",
  },
  {
    image: "https://images.pexels.com/photos/8438922/pexels-photo-8438922.jpeg?auto=compress&cs=tinysrgb&w=1920",
    title: "Website Development",
    highlight: "Powered by AI Workflows",
    subtitle: "Design and ship high-performance websites with intelligent search, personalization, and automated operations.",
  },
  {
    image: "https://images.unsplash.com/photo-1660165458059-57cfb6cc87e5?auto=format&fit=crop&w=1920&q=80",
    title: "Salesforce Transformation",
    highlight: "Using AI Agents and LLMs",
    subtitle: "Automate service, sales, and operations with agentic workflows built on your CRM and enterprise data.",
  },
];

const FEATURES = [
  { icon: Cloud,     title: "AI-Powered Mobile Application Development",  description: "Build iOS and Android products with AI-assisted development, test automation, and embedded copilots." },
  { icon: Cpu,       title: "AI-Powered Website Development",              description: "Deliver modern, conversion-focused websites with LLM search, personalization, and smart content workflows." },
  { icon: BarChart3, title: "Salesforce Automation with AI Agents",        description: "Deploy AI agents in Salesforce for lead qualification, case handling, and sales process acceleration." },
  { icon: Users,     title: "LLM Workflows for Teams",                     description: "Design secure prompt pipelines and AI copilots that help engineering, sales, and support teams execute faster." },
];

const STATS = [
  { number: "20+",   label: "Projects Delivered" },
  { number: "10+",   label: "Models in Production" },
  { number: "50+",   label: "Records Processed" },
  { number: "99.9%", label: "Platform Uptime" },
  { number: "20+",   label: "Enterprise Partners" },
];

const VISION_MISSION = [
  { icon: Eye,    title: "Strategy", description: "We align AI initiatives to measurable business outcomes with clear roadmaps and pilot-proof plans." },
  { icon: Target, title: "Build",    description: "We design production-grade models and data platforms with security, observability and explainability built-in." },
  { icon: Rocket, title: "Operate",  description: "We operationalize models with MLOps, monitoring and retraining workflows for reliable long-term value." },
];

const SERVICES = [
  { icon: Code,     title: "AI-Powered Mobile Apps", desc: "iOS/Android delivery with in-app agents" },
  { icon: Cloud,    title: "AI-Powered Websites",    desc: "High-performance sites with LLM workflows" },
  { icon: Zap,      title: "Salesforce AI Agents",   desc: "Agentic CRM automation and copilots" },
  { icon: Cpu,      title: "LLM Integration",        desc: "Model orchestration and retrieval" },
  { icon: Shield,   title: "AI Governance",          desc: "Security, observability, and compliance" },
];

const PARTNERS = [
  { name: "2Edge",       logo: "/assets/Company Logos/2edgelogo.png" },
  { name: "Alkatrading", logo: "/assets/Company Logos/Alkatrading-removebg-preview.png" },
  { name: "AVDMC",       logo: "/assets/Company Logos/AVDMC-removebg-preview.png" },
  { name: "Bare Nexus",  logo: "/assets/Company Logos/barenexus-removebg-preview.png" },
  { name: "Geetcare",    logo: "/assets/Company Logos/Geetcare-removebg-preview.png" },
  { name: "IOTIVITY",    logo: "/assets/Company Logos/IOTIVITY-removebg-preview.png" },
  { name: "JMF",         logo: "/assets/Company Logos/JMF-removebg-preview.png" },
  { name: "SBC",         logo: "/assets/Company Logos/sbc-removebg-preview.png" },
  { name: "Web",         logo: "/assets/Company Logos/web-removebg-preview.png" },
];

/* ----------------------- Subcomponents ----------------------------------- */

const LogoBadge = ({ src, name }) => (
  <div className="w-20 h-20 rounded-2xl bg-white border border-white/15 flex items-center justify-center overflow-hidden transition-all duration-300 group-hover:scale-105 group-hover:shadow-lg group-hover:shadow-purple-500/20">
    <img
      src={src}
      alt={name}
      loading="lazy"
      decoding="async"
      className="h-full w-full object-contain p-3 opacity-90 group-hover:opacity-100 transition-opacity"
    />
  </div>
);

/* ----------------------- Page -------------------------------------------- */

const LandingPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  const autoPlayRef = useRef(null);
  const resumeTimeoutRef = useRef(null);
  const heroRef = useRef(null);
  const testimonialsRef = useRef(null);
  const touchStartXRef = useRef(null);
  const touchStartYRef = useRef(null);

  /* Auto-play carousel */
  useEffect(() => {
    if (!isAutoPlaying) return;
    autoPlayRef.current = setInterval(() => {
      setCurrentSlide((p) => (p + 1) % HERO_SLIDES.length);
    }, 6000);
    return () => clearInterval(autoPlayRef.current);
  }, [isAutoPlaying]);

  useEffect(() => () => {
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
  }, []);

  const goToSlide = (index) => {
    if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    resumeTimeoutRef.current = setTimeout(() => setIsAutoPlaying(true), 12000);
  };
  const nextSlide = () => goToSlide((currentSlide + 1) % HERO_SLIDES.length);
  const prevSlide = () => goToSlide((currentSlide - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);

  const handleHeroTouchStart = (e) => {
    if (!e.touches || e.touches.length !== 1) return;
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
  };
  const handleHeroTouchEnd = (e) => {
    if (touchStartXRef.current === null) return;
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - touchStartXRef.current;
    const dy = t.clientY - touchStartYRef.current;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      dx < 0 ? nextSlide() : prevSlide();
    }
    touchStartXRef.current = null;
    touchStartYRef.current = null;
  };

  const scrollTestimonials = (direction) => {
    const c = testimonialsRef.current;
    if (!c) return;
    c.scrollBy({ left: direction === "left" ? -c.clientWidth : c.clientWidth, behavior: "smooth" });
  };

  /* Scroll-reveal observer for legacy classes */
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" },
    );
    document.querySelectorAll(".scroll-reveal, .scroll-reveal-left, .scroll-reveal-right").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  /* Cookie consent */
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        const consent = localStorage.getItem("cookieConsent");
        if (!consent) setShowCookieConsent(true);
      } catch {}
    }, 1500);
    return () => clearTimeout(t);
  }, []);

  const slide = HERO_SLIDES[currentSlide];

  return (
    <div className="min-h-screen text-[var(--text-primary)]">
      {/* ============================================================
          HERO
         ============================================================ */}
      <section
        ref={heroRef}
        className="relative flex min-h-[100svh] items-start justify-center overflow-hidden pb-28 pt-32 sm:items-center sm:pb-24 sm:pt-28 md:min-h-screen md:pt-24"
        data-testid="hero"
        onTouchStart={handleHeroTouchStart}
        onTouchEnd={handleHeroTouchEnd}
      >
        {/* Backgrounds */}
        <div className="absolute inset-0">
          <div className="hidden lg:block absolute inset-0">
            <VideoBackground
              src="/assets/138556-769988117.mp4"
              poster="https://images.pexels.com/photos/1181675/pexels-photo-1181675.jpeg?auto=compress&cs=tinysrgb&w=1600"
            />
          </div>

          <HeroAnimated />

          {HERO_SLIDES.map((s, i) => (
            <div
              key={i}
              className={`absolute inset-0 transition-opacity duration-1000 ease-out ${
                i === currentSlide ? "opacity-100" : "opacity-0"
              }`}
            >
              <img src={s.image} alt="" loading="eager" className="w-full h-full object-cover hero-slide-image" />
            </div>
          ))}

          <div className="absolute inset-0 hero-overlay" />
          {/* Aurora orbs in hero */}
          <GlowOrb color="purple" size={520} className="-top-40 -left-40" opacity={0.30} />
          <GlowOrb color="cyan"   size={460} className="-bottom-40 -right-40" opacity={0.22} />
        </div>

        {/* Content */}
        <div className="relative z-10 mx-auto w-full max-w-5xl px-5 text-center">
          <Reveal variant="up" duration={0.7}>
            <Eyebrow icon={Sparkles} className="mb-5">
              AI-Native Digital Engineering
            </Eyebrow>
          </Reveal>

          <div className="mx-auto w-full max-w-[1120px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col items-center"
              >
                <h1 className="mx-auto max-w-[14ch] text-balance font-display text-[clamp(2.75rem,7.5vw,5.75rem)] font-extrabold leading-[0.94] tracking-[-0.04em] sm:max-w-[15ch] md:max-w-[16ch] lg:max-w-[15ch] xl:max-w-none">
                  <span className="block">{slide.title}</span>
                  <span className="mt-3 block gradient-text">{slide.highlight}</span>
                </h1>
                <p className="mx-auto mt-6 max-w-3xl text-base leading-relaxed text-[var(--text-secondary)] sm:mt-5 sm:text-base md:text-lg">
                  {slide.subtitle}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          <Reveal variant="up" delay={0.15} duration={0.7}>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:mt-10 sm:flex-row">
              <Button asChild className="btn-primary h-12 w-full max-w-[18rem] rounded-full px-7 text-sm font-semibold sm:w-auto sm:max-w-none">
                <Link to="/contact" className="inline-flex items-center gap-2">
                  Book an AI Discovery Call
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="btn-glass h-12 w-full max-w-[18rem] rounded-full px-6 text-sm sm:w-auto sm:max-w-none">
                <Link to="/case-studies" className="inline-flex items-center gap-2">
                  <Play className="w-4 h-4" />
                  See Case Studies
                </Link>
              </Button>
            </div>
          </Reveal>

          {/* Trust pill */}
          <Reveal variant="up" delay={0.25}>
            <div className="mt-6 inline-flex max-w-full flex-wrap items-center justify-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-2 backdrop-blur-md sm:mt-8 sm:flex-nowrap">
              <div className="flex -space-x-2">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 border-2 border-[color:var(--page-bg)]"
                  />
                ))}
              </div>
              <span className="text-center text-xs text-[var(--text-secondary)]">
                Trusted by 50+ enterprise teams • 99.9% uptime
              </span>
            </div>
          </Reveal>
        </div>

        {/* Arrows */}
        <button
          onClick={prevSlide}
          aria-label="Previous slide"
          className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full btn-glass items-center justify-center"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={nextSlide}
          aria-label="Next slide"
          className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full btn-glass items-center justify-center"
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        {/* Dots */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-20">
          {HERO_SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => goToSlide(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`rounded-full transition-all duration-300 ${
                i === currentSlide
                  ? "w-8 h-2 bg-gradient-to-r from-[#9333EA] to-[#22D3EE]"
                  : "w-2 h-2 bg-white/30 hover:bg-white/50"
              }`}
            />
          ))}
        </div>
      </section>

      {/* ============================================================
          AI-POWERED SOLUTIONS
         ============================================================ */}
      <Section id="solutions" testId="solutions">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Left intro card */}
          <Reveal variant="left" duration={0.8}>
            <GlowCard className="p-8" hoverable={false}>
              <div className="relative h-56 mb-6 rounded-2xl overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/8386434/pexels-photo-8386434.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="AI Solutions"
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--page-bg)] via-[color:var(--page-bg)]/40 to-transparent" />
                <div className="absolute top-4 left-4">
                  <Eyebrow><Sparkles className="w-3 h-3" /> AI-Native</Eyebrow>
                </div>
              </div>
              <h2 className="font-display font-bold text-2xl sm:text-3xl mb-3 leading-tight">
                AI-Accelerated Engineering
                <br />
                <span className="gradient-text">for Mobile, Web, and Salesforce</span>
              </h2>
              <p className="text-[var(--text-secondary)] text-sm leading-relaxed">
                We combine product engineering with AI agents and LLM workflows to help teams ship faster, automate
                repetitive work, and focus on core business outcomes.
              </p>
            </GlowCard>
          </Reveal>

          {/* Right feature stack */}
          <FeatureStack features={FEATURES} />
        </div>
      </Section>

      {/* ============================================================
          COMPETITIVE EDGE
         ============================================================ */}
      <Section testId="competitive-edge">
        <Reveal variant="scale" duration={0.8}>
          <GlowCard variant="liquid" className="p-8 sm:p-12 aurora overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-10 items-center relative z-10">
              <div className="lg:order-2 flex justify-center">
                <div className="circular-visual w-64 h-64 lg:w-80 lg:h-80 relative">
                  <div className="absolute inset-0 rounded-full overflow-hidden">
                    <img
                      src="https://images.pexels.com/photos/8386365/pexels-photo-8386365.jpeg?auto=compress&cs=tinysrgb&w=600"
                      alt="AI delivery"
                      loading="lazy"
                      className="w-full h-full object-cover opacity-50"
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-cyan-500/10" />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center text-center p-8">
                    <div>
                      <h3 className="font-display font-bold text-lg">AI-Led Delivery for</h3>
                      <p className="gradient-text font-bold text-xl">Faster Time-to-Market</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:order-1">
                <Eyebrow icon={Rocket} className="mb-4">Outcome-Focused</Eyebrow>
                <h2 className="font-display font-bold text-2xl sm:text-3xl mb-4 leading-tight">
                  From discovery to production —{" "}
                  <span className="gradient-text">measurable, reliable, secure.</span>
                </h2>
                <p className="text-[var(--text-secondary)] text-sm sm:text-base leading-relaxed mb-6">
                  We deliver mobile apps, websites, and Salesforce automations with measurable outcomes and
                  enterprise-grade reliability. Pilots in weeks, not months.
                </p>
                <Button asChild className="btn-primary rounded-full px-6 h-11">
                  <Link to="/case-studies" className="inline-flex items-center gap-2">
                    See Case Studies
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </GlowCard>
        </Reveal>
      </Section>

      {/* ============================================================
          STATS
         ============================================================ */}
      <Section size="sm" testId="stats" className="relative">
        <SectionHeader
          eyebrow="By the numbers"
          eyebrowIcon={BarChart3}
          title={<>Leading with <span className="gradient-text">AI Innovation</span></>}
        />
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6 sm:gap-8">
          {STATS.map((s, i) => (
            <Reveal key={i} variant="up" delay={i * 0.06}>
              <div className="text-center">
                <AnimatedCounter value={s.number} label={s.label} />
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* ============================================================
          PARTNERS
         ============================================================ */}
      <Section size="sm" testId="partners">
        <SectionHeader
          eyebrow="Trusted Partners"
          title="Backed by enterprises & fast-moving startups"
          description="We deliver secure, reliable AI systems for regulated industries and product teams shipping at speed."
        />

        <Reveal variant="up">
          <GlowCard className="overflow-hidden p-6">
            <div className="relative w-full overflow-hidden py-2">
              <div className="marquee flex items-center gap-6">
                {[...PARTNERS, ...PARTNERS].map((p, i) => (
                  <div
                    key={i}
                    className="flex flex-col items-center justify-center mx-3 min-w-[120px] group"
                  >
                    <LogoBadge src={p.logo} name={p.name} />
                  </div>
                ))}
              </div>
              {/* Edge fades */}
              <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-[color:var(--surface-1)] to-transparent pointer-events-none" />
              <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-[color:var(--surface-1)] to-transparent pointer-events-none" />
            </div>
          </GlowCard>
        </Reveal>

        <div className="mt-8 flex justify-center">
          <Button asChild variant="outline" className="btn-outline rounded-full px-6">
            <Link to="/our-clients" className="inline-flex items-center gap-2">
              See our clients <ChevronRight className="w-4 h-4" />
            </Link>
          </Button>
        </div>
      </Section>

      {/* ============================================================
          STRATEGY · BUILD · OPERATE
         ============================================================ */}
      <Section id="vision" testId="vision-mission">
        <SectionHeader
          eyebrow="Our Approach"
          eyebrowIcon={Target}
          title={<>Strategy, Build, <span className="gradient-text">Operate</span></>}
          description="A repeatable, opinionated delivery model designed for outcomes — not demos."
        />
        <div className="grid md:grid-cols-3 gap-6">
          {VISION_MISSION.map((item, i) => (
            <Reveal key={i} variant="up" delay={i * 0.1}>
              <GlowCard className="vision-card h-full p-7 group">
                <div className="feature-icon feature-icon-lg mb-5 group-hover:scale-110 transition-transform">
                  <item.icon className="w-6 h-6" />
                </div>
                <h3 className="font-display font-bold text-xl mb-3">{item.title}</h3>
                <p className="text-[var(--text-secondary)] text-sm leading-relaxed">
                  {item.description}
                </p>
              </GlowCard>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* ============================================================
          TESTIMONIALS
         ============================================================ */}
      <Section testId="testimonials" className="overflow-hidden">
        <SectionHeader
          eyebrow="Client voices"
          eyebrowIcon={Star}
          title={<>What our <span className="gradient-text">Clients Say</span></>}
          description="Real outcomes — faster time-to-value and measurable ROI from practical AI deployments."
        />

        <div className="relative">
          {/* Edge fades */}
          <div className="absolute left-0 top-0 w-12 h-full bg-gradient-to-r from-[color:var(--page-bg)] to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 w-12 h-full bg-gradient-to-l from-[color:var(--page-bg)] to-transparent z-10 pointer-events-none" />

          {/* Arrows */}
          <button
            type="button"
            onClick={() => scrollTestimonials("left")}
            className="hidden md:flex items-center justify-center w-11 h-11 rounded-full btn-glass absolute left-2 top-1/2 -translate-y-1/2 z-20"
            aria-label="Previous testimonials"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => scrollTestimonials("right")}
            className="hidden md:flex items-center justify-center w-11 h-11 rounded-full btn-glass absolute right-2 top-1/2 -translate-y-1/2 z-20"
            aria-label="Next testimonials"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <div
            ref={testimonialsRef}
            className="flex gap-5 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-6 px-12 hide-scrollbar"
          >
            {landingTestimonials.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.55, delay: i * 0.04 }}
                className="min-w-[280px] sm:min-w-[340px] lg:min-w-[380px] glass-card testimonial-card p-7 snap-start"
              >
                <div className="flex items-center gap-3 mb-4">
                  {t.photo ? (
                    <img
                      src={t.photo}
                      alt={t.author}
                      loading="lazy"
                      className="w-12 h-12 rounded-full object-cover ring-2 ring-purple-500/30"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-cyan-600 flex items-center justify-center text-sm font-semibold text-white">
                      {t.author.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()}
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-sm text-[var(--text-primary)]">{t.author}</p>
                    <p className="text-xs text-[var(--text-secondary)]">{t.role}</p>
                    <p className="text-xs gradient-text-cool font-medium">{t.company}</p>
                  </div>
                </div>
                <div className="flex gap-0.5 mb-3">
                  {[...Array(t.rating)].map((_, k) => (
                    <Star key={k} className="w-4 h-4 fill-[var(--brand-primary-light)] text-[var(--brand-primary-light)]" />
                  ))}
                </div>
                <p className="text-[var(--text-secondary)] text-sm italic leading-relaxed">
                  "{t.quote}"
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ============================================================
          CORE OFFERINGS
         ============================================================ */}
      <Section testId="services">
        <SectionHeader
          eyebrow="Core Offerings"
          eyebrowIcon={Cpu}
          title={<>Built for <span className="gradient-text">Business Impact</span></>}
        />
        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-5">
          {SERVICES.map((s, i) => (
            <Reveal key={i} variant="up" delay={i * 0.06}>
              <GlowCard className="text-center p-6 h-full group">
                <div className="feature-icon mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <s.icon className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-sm mb-1.5 leading-snug">{s.title}</h3>
                <p className="text-[var(--text-secondary)] text-xs leading-relaxed">{s.desc}</p>
              </GlowCard>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* ============================================================
          BIG CTA
         ============================================================ */}
      <Section testId="cta">
        <Reveal variant="up" duration={0.8}>
          <GlowCard variant="liquid" className="aurora overflow-hidden p-0">
            <div className="grid lg:grid-cols-2 gap-0 items-center">
              <div className="p-8 sm:p-12 flex flex-col justify-center relative z-10">
                <Eyebrow icon={Sparkles} className="mb-4">Let's build</Eyebrow>
                <h3 className="font-display font-bold text-3xl sm:text-4xl mb-4 leading-tight">
                  Ready to scale delivery with{" "}
                  <span className="gradient-text">AI agents and LLM workflows?</span>
                </h3>
                <p className="text-[var(--text-secondary)] text-base mb-7 leading-relaxed">
                  Let us map your next mobile app, website, or Salesforce automation initiative to clear business outcomes.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button asChild className="btn-primary rounded-full px-6 h-12">
                    <Link to="/contact" className="inline-flex items-center gap-2">
                      Book an AI Discovery Call <ArrowRight className="w-4 h-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="btn-outline rounded-full px-6 h-12">
                    <Link to="/case-studies">See case studies</Link>
                  </Button>
                </div>
              </div>

              <div className="relative h-64 lg:h-full min-h-[260px] overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?auto=compress&cs=tinysrgb&w=1600"
                  alt="AI collaborative team"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-l from-transparent via-[color:var(--page-bg)]/30 to-[color:var(--page-bg)]/80" />
              </div>
            </div>
          </GlowCard>
        </Reveal>
      </Section>

      {/* ============================================================
          COOKIE CONSENT
         ============================================================ */}
      <AnimatePresence>
        {showCookieConsent && (
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 60 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-0 left-0 right-0 z-50 p-4"
            data-testid="cookie-consent"
          >
            <div className="max-w-3xl mx-auto liquid-glass p-5 rounded-2xl shadow-2xl">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                <div className="flex-1">
                  <h4 className="font-semibold text-sm mb-1">Your cookie preferences</h4>
                  <p className="text-xs text-[var(--text-secondary)]">
                    We use cookies to enhance your browsing experience.{" "}
                    <Link to="/privacy" className="text-[var(--brand-primary-light)] hover:underline">
                      Privacy Policy
                    </Link>{" "}
                    ·{" "}
                    <Link to="/cookies" className="text-[var(--brand-primary-light)] hover:underline">
                      Cookie Policy
                    </Link>
                  </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={() => {
                      try { localStorage.setItem("cookieConsent", "denied"); } catch {}
                      setShowCookieConsent(false);
                    }}
                    variant="outline"
                    size="sm"
                    className="btn-outline rounded-full text-xs px-4 h-8"
                  >
                    Deny
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="btn-outline rounded-full text-xs px-4 h-8 inline-flex items-center"
                  >
                    <Settings className="w-3 h-3 mr-1.5" /> Manage
                  </Button>
                  <Button
                    onClick={() => {
                      try { localStorage.setItem("cookieConsent", "accepted"); } catch {}
                      setShowCookieConsent(false);
                    }}
                    size="sm"
                    className="btn-primary rounded-full text-xs px-4 h-8"
                  >
                    Accept all
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LandingPage;
